// Gemeinsame Karten-Sprache des Blog-Cockpits — EINE visuelle Vokabel für
// Backlog, Topic-Detail, Claims-Archiv und Trend-Inbox (Doktrin: keine drei
// Dialekte, präsentations-first, semantische Tokens, hell+dunkel).
//
// Alles hier ist reines Token-Konsum-Styling; keine Modals. Detail passiert auf
// eigenen Routen / inline, nie in einem Pop-up.
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  AlertTriangle,
  ArrowLeft,
  ShieldCheck,
  ShieldAlert,
  FileText,
  Atom,
  Image as ImageIcon,
} from "lucide-react";
import { useT } from "../../i18n";

// ── Status ───────────────────────────────────────────────────────────────────
export const STATUS_ORDER = [
  "backlog",
  "planned",
  "generating",
  "draft",
  "review",
  "approved",
  "published",
] as const;
export type TopicStatus = (typeof STATUS_ORDER)[number];

const STATUS_TONE: Record<string, string> = {
  backlog: "bg-surface-3 text-text-2",
  planned: "bg-info-soft text-info",
  generating: "bg-warning-soft text-warning animate-pulse",
  draft: "bg-warning-soft text-warning",
  review: "bg-accent-500/15 text-accent-700 dark:text-accent-300",
  approved: "bg-success-soft text-success",
  published: "bg-success text-white",
};

export function statusTone(status: string): string {
  return STATUS_TONE[status] ?? "bg-surface-3 text-text-2";
}

// ── Rubrik-Label (English-only-UI) ────────────────────────────────────────────
// Die benefit_area steht in der DB deutsch; die UI ist english only. Spiegelt
// app/services/blog_evi_dataviz.py::BENEFIT_AREA_EN (6 feste Rubriken). Unbekannte
// Werte fallen 1:1 durch. In JEDER Cockpit-Rubrik-Überschrift verwenden.
const BENEFIT_AREA_EN: Record<string, string> = {
  "Feuchtigkeit & Barriere": "Hydration & Barrier",
  "Beruhigung & Rötung": "Soothing & Redness",
  "Anti-Pollution": "Anti-Pollution",
  "Licht- & DNA-Schutz": "Light & DNA Protection",
  "Anti-Aging": "Anti-Aging",
  "Zell- & DNA-Schutz": "Cell & DNA Protection",
  "Formulierung & Regulatorik": "Formulation & Regulatory",
};

export function benefitAreaLabel(area: string | null | undefined): string {
  if (!area) return "";
  return BENEFIT_AREA_EN[area.trim()] ?? area;
}

// ── Trend↔Claim-Match-Badge ───────────────────────────────────────────────────
// Zeigt, wie ein Trend zu UNSEREN Ectoin-Claims steht (radar-seitig LLM-bewertet):
// direct = wir haben einen Claim dafür · adjacent = benachbart, kein direkter Claim
// · none = off-domain. null (noch nicht bewertet) → nichts rendern.
const MATCH_TONE: Record<string, { cls: string; label: string }> = {
  direct: { cls: "bg-success-soft text-success", label: "Overlaps our claims" },
  adjacent: { cls: "bg-warning-soft text-warning", label: "Adjacent" },
  none: { cls: "bg-surface-3 text-text-3", label: "Off-domain" },
};

export function TrendMatchBadge({ match }: { match: string | null | undefined }) {
  if (!match || !MATCH_TONE[match]) return null;
  const m = MATCH_TONE[match];
  return (
    <span className={clsx("inline-flex items-center rounded-chip px-2 py-0.5 text-[0.68rem] font-medium", m.cls)}>
      {m.label}
    </span>
  );
}

export function CertOverlapBadges({ certs }: { certs: string[] | null | undefined }) {
  if (!certs || certs.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {certs.map((c) => (
        <span
          key={c}
          className="inline-flex items-center gap-1 rounded-chip bg-accent-500/12 px-1.5 py-0.5 text-[0.66rem] font-medium text-accent-700 dark:text-accent-300"
        >
          <ShieldCheck className="h-3 w-3" />
          {c}
        </span>
      ))}
    </span>
  );
}

export function StatusPill({ status, className }: { status: string; className?: string }) {
  const t = useT();
  const label = (t.blog.status as Record<string, string>)[status] ?? status;
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        statusTone(status),
        className
      )}
    >
      {label}
    </span>
  );
}

export function TopicClassBadge({ topicClass }: { topicClass: string }) {
  const t = useT();
  const isKuer = topicClass === "kuer";
  const label = (t.blog.topicClass as Record<string, string>)[topicClass] ?? topicClass;
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-chip px-1.5 py-0.5 text-[0.68rem] font-semibold uppercase tracking-wider",
        isKuer
          ? "bg-accent-500/12 text-accent-700 dark:text-accent-300"
          : "bg-surface-3 text-text-3"
      )}
    >
      {label}
    </span>
  );
}

// ── Eyebrow / Section heading (eine Typo-Hierarchie überall) ──────────────────
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-text-3">
      {children}
    </div>
  );
}

export function RubrikHeading({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-baseline gap-2 border-b border-border pb-2 mb-3">
      <h2 className="font-display text-base font-semibold text-text">{title}</h2>
      <span className="font-numeric text-sm text-text-3 tabular-nums">{count}</span>
    </div>
  );
}

// ── Zurück-Link (Detail → Cockpit-Tab) ────────────────────────────────────────
// EINE Implementierung für alle Blog-Detail-Seiten (Claim/Topic/Trend). `to`
// trägt den Ziel-Tab (z. B. /blog/dashboard?tab=claims), damit der Rücksprung
// auf der Rubrik landet, aus der der Nutzer kam — nicht auf dem Default-Tab.
export function BlogBackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1.5 text-sm text-text-3 hover:text-text">
      <ArrowLeft className="h-4 w-4" /> {label}
    </Link>
  );
}

// ── Karten-Shell (die eine Karte) ─────────────────────────────────────────────
export function CardShell({
  children,
  onClick,
  interactive,
  accent,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  interactive?: boolean;
  /** linker Akzentstreifen (z. B. success/warning für Vorbehalt) */
  accent?: string;
  className?: string;
}) {
  const base = clsx(
    "rounded-card border border-border bg-surface p-4 text-left w-full",
    accent && "border-l-4",
    accent,
    interactive &&
      "transition-colors hover:border-accent-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/50 cursor-pointer",
    className
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={base}>
        {children}
      </button>
    );
  }
  return <div className={base}>{children}</div>;
}

// ── Evidenz-Karte (evi-Claim) — presentation-first, für Topic-Detail UND Archiv ─
export interface EviClaimLike {
  id: string;
  consumer_headline: string;
  effect_value?: string | null;
  strength_tier?: string | null;
  evidence_tier_label?: string | null;
  significance_gate?: string | null;
  has_caveat?: boolean;
  caveat?: string | null;
  study_title?: string | null;
  endpoint_code?: string | null;
}

export function EvidenceCard({ claim }: { claim: EviClaimLike }) {
  const t = useT();
  const caveat = claim.has_caveat && claim.caveat ? claim.caveat : null;
  // effect_value ist KEINE garantiert-kurze Kennzahl — mal „−48 %", mal ein
  // ganzer Satz. Nur die kurze Form als Akzent-Chip zeigen; die lange Aussage
  // trägt ohnehin die consumer_headline (content-first, bricht nie).
  const shortEffect =
    claim.effect_value && claim.effect_value.trim().length <= 22
      ? claim.effect_value.trim()
      : null;
  return (
    <CardShell accent={caveat ? "border-l-warning" : "border-l-success"}>
      <div className="font-display text-[0.98rem] font-semibold leading-snug text-text">
        {claim.consumer_headline}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-3">
        {shortEffect && (
          <span className="rounded-chip bg-accent-500/12 px-1.5 py-0.5 font-numeric font-semibold tabular-nums text-accent-700 dark:text-accent-300">
            {shortEffect}
          </span>
        )}
        {claim.strength_tier && (
          <span className="rounded-chip bg-surface-2 px-1.5 py-0.5 font-mono uppercase tracking-wide">
            {claim.strength_tier}
          </span>
        )}
        {claim.evidence_tier_label && <span>{claim.evidence_tier_label}</span>}
        {claim.significance_gate && (
          <span className="font-mono lowercase">{claim.significance_gate}</span>
        )}
        {claim.study_title && <span className="max-w-full truncate">{claim.study_title}</span>}
      </div>
      {caveat && (
        <div className="mt-2 flex items-start gap-1.5 text-xs">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
          <span className="text-text-2">
            <span className="font-medium text-warning">{t.blog.claims.caveat}: </span>
            {caveat}
          </span>
        </div>
      )}
    </CardShell>
  );
}

// ── Cert-Chips (Tags, nie eine zweite Achse) ──────────────────────────────────
export interface CertChip {
  axis: string;
  label: string;
  cert_status: string;
  cert_ref?: string | null;
  cert_expiry?: string | null;
}

function certMeta(status: string) {
  if (status === "third_party_cert")
    return { key: "certified" as const, cls: "bg-success-soft text-success", Icon: ShieldCheck };
  if (status === "self_declared")
    return { key: "selfDeclared" as const, cls: "bg-warning-soft text-warning", Icon: ShieldAlert };
  return { key: "dataStatement" as const, cls: "bg-surface-3 text-text-2", Icon: FileText };
}

export function CertChips({ chips }: { chips: CertChip[] }) {
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((c) => {
        const m = certMeta(c.cert_status);
        return (
          <span
            key={c.axis}
            title={c.cert_ref ?? undefined}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-chip px-2 py-1 text-xs font-medium",
              m.cls
            )}
          >
            <m.Icon className="h-3.5 w-3.5" />
            {c.label}
          </span>
        );
      })}
    </div>
  );
}

// ── MoA-Karte (Mechanismus-Ebene neben der Evidenz) ───────────────────────────
// EINE Karten-Sprache: dieselbe Vokabel wie EvidenceCard, aber die Mechanismus-
// Sicht. Zeigt title · moa_short · benefit · die verallgemeinerten Effekt-Aussagen;
// Fußnoten sichtbar als Kleingedrucktes, Provenance (Re-Ableitungs-Spur) in einem
// <details>. Das Bild (Phase 3) ist noch NULL → Alt-Text-Platzhalter.
export interface MoAProvenance {
  source?: string | null;
  original_figure?: string | null;
  flag?: string | null;
}
export interface MoAEffectExample {
  marker?: string | null;
  statement?: string | null;
  evidence_class?: string | null;
  footnote?: string | null;
  provenance?: MoAProvenance | null;
}
export interface MoACardData {
  id: string;
  slug: string;
  rubric?: string | null;
  theme?: string | null;
  title: string;
  subtitle?: string | null;
  moa_short: string;
  moa_technical?: string | null;
  benefit: string;
  skin_bridge?: string | null;
  effect_examples: MoAEffectExample[];
  image_key?: string | null;
  image_url?: string | null;
  image_alt?: string | null;
  status?: string | null;
}

// device-illustration = gefußnotetes Beispiel der Mechanismus-Stärke aus einer
// Medizinprodukt-Studie (keine Kosmetik-Aussage) → eigener, zurückhaltender Ton.
function evidenceClassTone(cls: string | null | undefined): string {
  return cls === "device-illustration"
    ? "bg-surface-3 text-text-3"
    : "bg-success-soft text-success";
}

export function MoACard({ card }: { card: MoACardData }) {
  const examples = card.effect_examples ?? [];
  const hasProvenance = examples.some(
    (e) => e.footnote || e.provenance?.source || e.provenance?.original_figure
  );
  return (
    <CardShell accent="border-l-accent-500" className="space-y-3">
      {/* Header: mechanism eyebrow + rubric chip + title */}
      <div>
        <div className="flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-accent-700 dark:text-accent-300">
          <Atom className="h-3.5 w-3.5" />
          Mechanism of action
          {card.rubric && (
            <span className="ml-auto rounded-chip bg-surface-2 px-1.5 py-0.5 text-[0.62rem] font-medium normal-case tracking-normal text-text-2">
              {benefitAreaLabel(card.rubric)}
            </span>
          )}
        </div>
        <h3 className="mt-1 font-display text-[1rem] font-semibold leading-snug text-text">
          {card.title}
        </h3>
        {card.subtitle && (
          <p className="mt-0.5 text-[0.8rem] leading-snug text-text-3">{card.subtitle}</p>
        )}
      </div>

      {/* Generated MoA image (Phase 3) — or a placeholder until it's rendered. */}
      {card.image_url ? (
        <img
          src={card.image_url}
          alt={card.image_alt || `Mechanism of action: ${card.title}`}
          loading="lazy"
          className="aspect-video w-full rounded-card border border-border object-cover"
        />
      ) : (
        <div className="flex items-start gap-2 rounded-card border border-dashed border-border bg-surface-2/50 px-3 py-2 text-xs text-text-3">
          <ImageIcon className="mt-px h-4 w-4 shrink-0 opacity-60" />
          <span className="leading-snug">{card.image_alt || "MoA illustration coming soon."}</span>
        </div>
      )}

      {/* The mechanism, in plain words */}
      <p className="text-[0.86rem] leading-6 text-text-2">{card.moa_short}</p>

      {/* The material skin benefit */}
      <div className="rounded-card bg-accent-500/8 px-3 py-2">
        <div className="text-[0.66rem] font-semibold uppercase tracking-wider text-accent-700 dark:text-accent-300">
          Skin benefit
        </div>
        <p className="mt-0.5 text-[0.84rem] leading-snug text-text-2">{card.benefit}</p>
      </div>

      {/* Generalized effect statements (the LLM-safe evidence) */}
      {examples.length > 0 && (
        <ul className="space-y-2">
          {examples.map((ex, i) => (
            <li key={ex.marker ?? i} className="flex gap-2 text-[0.82rem] leading-6 text-text-2">
              <span className="mt-0.5 shrink-0 font-numeric text-[0.7rem] font-semibold tabular-nums text-text-3">
                {ex.marker ?? i + 1}
              </span>
              <span>
                {ex.statement}
                {ex.evidence_class && (
                  <span
                    className={clsx(
                      "ml-1.5 inline-flex items-center rounded-chip px-1.5 py-0.5 align-middle text-[0.6rem] font-medium uppercase tracking-wide",
                      evidenceClassTone(ex.evidence_class)
                    )}
                  >
                    {ex.evidence_class === "device-illustration" ? "illustration" : "study"}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Footnotes + provenance (the re-derivation trail) — visible small-print */}
      {hasProvenance && (
        <details className="group border-t border-border pt-2">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[0.7rem] font-medium text-text-3 hover:text-text-2">
            <FileText className="h-3 w-3" />
            Sources &amp; method
            <span className="ml-auto text-text-3/70 group-open:hidden">show</span>
            <span className="ml-auto hidden text-text-3/70 group-open:inline">hide</span>
          </summary>
          <div className="mt-2 space-y-2">
            {examples.map((ex, i) => {
              if (!ex.footnote && !ex.provenance?.source && !ex.provenance?.original_figure) {
                return null;
              }
              return (
                <div key={ex.marker ?? i} className="text-[0.7rem] leading-5 text-text-3">
                  {ex.footnote && (
                    <p>
                      <span className="font-numeric font-semibold tabular-nums">{ex.marker ?? i + 1}</span>{" "}
                      {ex.footnote}
                    </p>
                  )}
                  {(ex.provenance?.source || ex.provenance?.original_figure) && (
                    <p className="mt-0.5 text-text-3/80">
                      {ex.provenance?.flag && (
                        <span className="mr-1 font-mono uppercase tracking-wide">[{ex.provenance.flag}]</span>
                      )}
                      {ex.provenance?.original_figure}
                      {ex.provenance?.source && (
                        <span className="italic"> — {ex.provenance.source}</span>
                      )}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </details>
      )}
    </CardShell>
  );
}

// ── Pipeline-Stepper (Status-Fluss) ───────────────────────────────────────────
// Wickelt in schmalen Spalten (z. B. die 360px-Schaltzentrale) elegant auf einen
// ZWEIZEILIGEN Pfad um — kein horizontaler Schieberegler. Der Verbinder sitzt am
// führenden Rand jedes Folge-Pills, damit ein Zeilenumbruch keine baumelnde Linie
// hinterlässt.
export function PipelineStepper({ status }: { status: string }) {
  const t = useT();
  const activeIdx = STATUS_ORDER.indexOf(status as TopicStatus);
  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
      {STATUS_ORDER.map((s, i) => {
        const done = activeIdx >= 0 && i < activeIdx;
        const active = i === activeIdx;
        const label = (t.blog.status as Record<string, string>)[s];
        return (
          <div key={s} className="flex items-center gap-1">
            {i > 0 && (
              <span className={clsx("h-px w-2.5 shrink-0", done ? "bg-success/50" : "bg-border")} />
            )}
            <span
              className={clsx(
                "rounded-full px-2 py-0.5 text-[0.7rem] font-medium whitespace-nowrap",
                active && statusTone(s),
                done && "bg-success-soft text-success",
                !active && !done && "bg-surface-2 text-text-3"
              )}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
